<script lang="ts">
  import clsx from 'clsx';
  import { Paragraph } from '@athena-lib/Text';
  import { Image } from '@athena-lib/Image';
  import { objectKeys } from '@athena-lib/object-utils';
  import type { DeviceNameWithoutDefault } from '@athena-lib/device';
  import { getResponsiveClasses } from '@athena-lib/responsive-utils';
  import type { Alignment, UspProps } from './types';

  const {
    heading,
    image: image,
    text,
    class: className,
    iconAbove,
    alignment,
  }: UspProps = $props();

  const responsiveIconAboveClasses = {
    default: 'flex-col',
    sm: 'flex-col',
    md: 'md:flex-col',
    lg: 'lg:flex-col',
  };
  const responsiveSideAboveClasses = {
    default: 'flex-row',
    sm: 'flex-row',
    md: 'md:flex-row',
    lg: 'lg:flex-row',
  };

  const responsiveAlignClasses: Record<
    'default' | DeviceNameWithoutDefault,
    Record<Alignment, string>
  > = {
    default: {
      left: 'text-left items-start justify-start',
      center: 'text-center items-center justify-center',
      right: 'text-right items-end justify-end',
    },
    md: {
      left: 'md:text-left md:items-start md:justify-start',
      center: 'md:text-center md:items-center md:justify-center',
      right: 'md:text-right md:items-end md:justify-end',
    },
    lg: {
      left: 'lg:text-left lg:items-start lg:justify-start',
      center: 'lg:text-center lg:items-center lg:justify-center',
      right: 'lg:text-right lg:items-end lg:justify-end',
    },
  };

  const iconClasses = $derived.by(() => {
    const classes: string[] = [];
    if (typeof iconAbove === 'boolean') {
      classes.push(
        iconAbove ? responsiveIconAboveClasses.default : responsiveSideAboveClasses.default,
      );
    } else if (typeof iconAbove === 'object') {
      for (const deviceName of objectKeys(iconAbove)) {
        classes.push(
          iconAbove[deviceName]
            ? responsiveIconAboveClasses[deviceName]
            : responsiveSideAboveClasses[deviceName],
        );
      }
    }

    return clsx(classes);
  });
</script>

<div
  class={clsx(
    'flex h-full gap-6',
    iconClasses,
    className,
    getResponsiveClasses(alignment, responsiveAlignClasses),
  )}
>
  {#if image.src}
    <div class={clsx('flex h-auto w-7 flex-shrink-0 flex-row items-center md:w-9')}>
      <Image src={image.src} alt={image.alt ?? image.src} loading="lazy" widthOfContainer={0.2} />
    </div>
  {/if}
  <div>
    <Paragraph class="!mb-1 !leading-4"><strong>{heading}</strong></Paragraph>
    <Paragraph>{text}</Paragraph>
  </div>
</div>
